import React from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useLanguage } from '../context/LanguageContext';

export function ChatReportToolbar({
  from,
  to,
  onFrom,
  onTo,
  onLoad,
  loading,
  showLoadButton = true,
  children,
}) {
  const { t } = useLanguage();
  return (
    <div className="chat-app-toolbar">
      <div className="chat-app-toolbar-dates">
        <div className="chat-app-toolbar-field">
          <label className="chat-app-field-label">{t('chatApp.periodFrom')}</label>
          <Form.Control type="date" value={from} onChange={(e) => onFrom(e.target.value)} />
        </div>
        <div className="chat-app-toolbar-field">
          <label className="chat-app-field-label">{t('chatApp.periodTo')}</label>
          <Form.Control type="date" value={to} onChange={(e) => onTo(e.target.value)} />
        </div>
        {showLoadButton && onLoad && (
          <Button size="sm" className="btn-gradient chat-app-toolbar-load" onClick={onLoad} disabled={loading}>
            {loading ? <Spinner size="sm" animation="border" /> : t('chatApp.load')}
          </Button>
        )}
      </div>
      {children}
    </div>
  );
}

export function ChatTableToolbar({
  search,
  onSearch,
  onSearchSubmit,
  type,
  onType,
  typeOptions,
  onExport,
  exporting,
}) {
  const { t } = useLanguage();
  return (
    <div className="chat-app-table-toolbar">
      <Form.Control
        size="sm"
        type="search"
        className="chat-app-table-search"
        placeholder={t('billing.searchProductBarcode')}
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onSearchSubmit) onSearchSubmit();
        }}
      />
      {typeOptions && (
        <Form.Select
          size="sm"
          className="chat-app-table-type"
          value={type || ''}
          onChange={(e) => onType(e.target.value)}
        >
          <option value="">{t('billing.allTypes')}</option>
          {typeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Select>
      )}
      {onExport && (
        <Button size="sm" variant="outline-success" onClick={onExport} disabled={exporting}>
          {exporting ? <Spinner size="sm" animation="border" /> : t('billing.downloadExcel')}
        </Button>
      )}
    </div>
  );
}

export function ChatSummaryCards({ items }) {
  return (
    <div className="chat-app-summary-grid">
      {items.map((item) => (
        <div key={item.key} className={`chat-app-summary-card ${item.variant || ''}`}>
          <div className="chat-app-summary-card-top">
            <div className="chat-app-summary-value">{item.value}</div>
            {item.help && (
              <button
                type="button"
                className="chat-app-summary-help"
                title={item.help}
                aria-label={item.help}
              >
                ?
              </button>
            )}
          </div>
          <div className="chat-app-summary-label">{item.label}</div>
        </div>
      ))}
    </div>
  );
}

export function ChatDataTable({ columns, rows, emptyText, getRowKey }) {
  const { t } = useLanguage();
  const empty = emptyText || t('chatApp.noData');
  return (
    <div className="chat-app-table-wrap">
      <table className="chat-app-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={[
                  col.align === 'end' ? 'text-end' : '',
                  col.numeric ? 'chat-table-col-numeric' : '',
                ].filter(Boolean).join(' ') || undefined}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="chat-app-table-empty">
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row, idx) => (
              <tr key={getRowKey ? getRowKey(row, idx) : idx}>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={[
                      col.align === 'end' ? 'text-end' : '',
                      col.numeric ? 'chat-table-col-numeric' : '',
                    ].filter(Boolean).join(' ') || undefined}
                  >
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export function ChatPanel({ title, children, actions }) {
  return (
    <section className="chat-app-panel">
      {(title || actions) && (
        <header className="chat-app-panel-header">
          {title && <h3 className="chat-app-panel-title">{title}</h3>}
          {actions}
        </header>
      )}
      <div className="chat-app-panel-body">{children}</div>
    </section>
  );
}

export function ChatPagination({
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 15, 25, 50],
}) {
  const { t } = useLanguage();
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(page, totalPages);
  const from = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, totalItems);

  return (
    <div className="chat-app-pagination">
      <span className="chat-app-pagination-info">
        {t('chatApp.showingItems', { from, to, total: totalItems })}
      </span>
      <div className="chat-app-pagination-controls">
        {onPageSizeChange && (
          <Form.Select
            size="sm"
            className="chat-app-page-size"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Form.Select>
        )}
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
        >
          ‹
        </Button>
        <span className="chat-app-pagination-page">
          {safePage} / {totalPages}
        </span>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={safePage >= totalPages}
          onClick={() => onPageChange(safePage + 1)}
        >
          ›
        </Button>
      </div>
    </div>
  );
}

export function ChatSubTabs({ tabs, active, onChange }) {
  return (
    <div className="chat-app-subtabs" role="tablist">
      {tabs.map((tab) => {
        const count = tab.count;
        const hasCount = count != null && count !== '';
        const highlight = tab.highlight || tab.id === 'pendingDecision';
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active === tab.id}
            className={[
              'chat-app-subtab',
              active === tab.id ? 'active' : '',
              highlight ? 'chat-app-subtab--pending' : '',
            ].filter(Boolean).join(' ')}
            onClick={() => onChange(tab.id)}
          >
            <span className="chat-app-subtab-label">{tab.label}</span>
            {hasCount && (
              <span className="chat-app-subtab-count">{count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
